import type {
  MessageDecoder,
  MessageEncoder,
  TrackReferenceOrPlaceholder,
  WidgetState,
} from "@livekit/components-core"
import {
  isEqualTrackRef,
  isTrackReference,
  isWeb,
  log,
} from "@livekit/components-core"
import {
  ConnectionStateToast,
  LayoutContextProvider,
  MessageFormatter,
  RoomAudioRenderer,
  useChat,
  useCreateLayoutContext,
  useDataChannel,
  useParticipants,
  usePinnedTracks,
  useRoomContext,
  useTracks,
} from "@livekit/components-react"
import { useMediaQuery, useTheme } from "@mui/material"
import * as Sentry from "@sentry/react"
import {
  LocalParticipant,
  RemoteParticipant,
  RoomEvent,
  Track,
} from "livekit-client"
import * as React from "react"
import Background from "../../components/background"
import CallLoading from "../../components/CustomVideoConference/CallLoading"
import {
  MobileLayout,
  WebLayout,
} from "../../components/CustomVideoConference/Layouts"
import Overlay from "../../components/Modal/Overlay"
import { useCustomBranding } from "../../constants/colors"
import { checkCallExists } from "../../external/firestore"
import { AnalyticsEvents } from "../../types/Analytics"
import {
  CallDisconnectType,
  ChatMessage,
  ChatMessageInputType,
  ChatMessageSender,
} from "../../types/Call"
import { postMessageToParent } from "../../utils/func"
import { useAppContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"
import "./MyVideoConference.css"

/**
 * @public
 */
export interface VideoConferenceProps
  extends React.HTMLAttributes<HTMLDivElement> {
  setParticipants: (
    participants: (RemoteParticipant | LocalParticipant)[],
  ) => void
  duration: number
  maxDuration: number
  onUserMessage?: () => void
  onDisconnected?: (callDisconnectType: CallDisconnectType | null) => void
  onParticipantSpeaking?: (speakingParticipantIds: string[]) => void
  chatMessageFormatter?: MessageFormatter
  chatMessageEncoder?: MessageEncoder
  chatMessageDecoder?: MessageDecoder
  /** @alpha */
  SettingsComponent?: React.ComponentType
  isEmbed: boolean
}

export function MyVideoConference({
  chatMessageFormatter,
  chatMessageDecoder,
  chatMessageEncoder,
  setParticipants,
  duration,
  maxDuration,
  onUserMessage,
  onDisconnected,
  onParticipantSpeaking,
  SettingsComponent,
  isEmbed,
  ...props
}: VideoConferenceProps) {
  const [showChat, setShowChat] = React.useState(false)
  const [hostJoined, setHostJoined] = React.useState(false)
  const initTime = React.useRef(new Date())
  const hasLoggedHostJoin = React.useRef(false)
  const [speakingParticipantIds, setSpeakingParticipantIds] = React.useState<
    string[]
  >([])

  const context = useAppContext()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const { chatMessages, send, isSending, update } = useChat()
  const participants = useParticipants()

  const room = useRoomContext()

  const { logAnalyticsEvent } = useMixpanel()

  React.useEffect(() => {
    Sentry.addBreadcrumb({
      category: "video-conference",
      message: "Video conference initialized",
      level: "info",
      data: {
        initializationTime: initTime.current.toISOString(),
      },
    })
  }, [])

  React.useEffect(() => {
    const checkCall = async () => {
      const roomId = room.name
      if (!roomId && onDisconnected) {
        onDisconnected(CallDisconnectType.Other)
        return
      }

      const exists = await checkCallExists(roomId)
      if (!exists && onDisconnected) {
        room.disconnect()
        onDisconnected(CallDisconnectType.Other)
      }
    }
    checkCall()
  }, [])

  React.useEffect(() => {
    setParticipants(participants)

    const agent = participants.find((p) => p.isAgent)
    if (agent && !hasLoggedHostJoin.current) {
      const isAgentReady =
        agent.attributes &&
        (agent.attributes["lk.agent.state"] === "speaking" ||
          agent.attributes["lk.agent.state"] === "ready" ||
          agent.attributes["lk.agent.state"] === "thinking" ||
          agent.attributes["lk.agent.state"] === "listening")

      if (isAgentReady) {
        setHostJoined(true)
        hasLoggedHostJoin.current = true
        const hostJoinTime = new Date()

        Sentry.addBreadcrumb({
          category: "video-conference",
          message: "Host joined conference",
          level: "info",
          data: {
            hostJoinTime: hostJoinTime.toISOString(),
            timeTakenMs: hostJoinTime.getTime() - initTime.current.getTime(),
          },
        })

        // Notify parent iframe that agent has joined
        postMessageToParent({
          type: "AGENT_JOINED",
          callId: context.call?.id,
        })
      }
    }
  }, [participants])

  React.useEffect(() => {
    const speakingIds = participants
      .filter((p) => p.isSpeaking)
      .map((p) => p.identity)

    setSpeakingParticipantIds(speakingIds)

    if (onParticipantSpeaking) {
      onParticipantSpeaking(speakingIds)
    }
  }, [participants, onParticipantSpeaking])

  const lastProcessedMessage = React.useRef<number>(0)

  React.useEffect(() => {
    const req = async () => {
      if (chatMessages.length === 0) {
        return
      }
      const lastMessage = chatMessages[chatMessages.length - 1]

      if (lastMessage.timestamp === lastProcessedMessage.current) {
        return
      }
      lastProcessedMessage.current = lastMessage.timestamp

      const messageToSend: ChatMessage = {
        message: lastMessage.message,
        sentAt: new Date(lastMessage.timestamp),
        sender: lastMessage.from.isAgent
          ? ChatMessageSender.ai
          : ChatMessageSender.user,
      }

      if (messageToSend.sender === ChatMessageSender.user) {
        onUserMessage?.()
      }

      const eventToSend =
        messageToSend.sender === ChatMessageSender.ai
          ? chatMessages.length === 1
            ? AnalyticsEvents.GreetingMessage
            : AnalyticsEvents.AnswerProvided
          : AnalyticsEvents.QuestionAsked
      logAnalyticsEvent(
        eventToSend,
        eventToSend === AnalyticsEvents.QuestionAsked
          ? {
              input_type: ChatMessageInputType.voice,
            }
          : undefined,
      )
    }
    req()
  }, [chatMessages])

  const onMessage = (msg: any) => {
    const message = new TextDecoder("utf-8").decode(msg.payload)
    send(message)
  }

  useDataChannel("transcript", onMessage)
  const { message: latestMessage } = useDataChannel("transcript", (msg) =>
    console.log("message received", msg),
  )

  const [widgetState, setWidgetState] = React.useState<WidgetState>({
    showChat: true,
    unreadMessages: 0,
    showSettings: false,
  })
  const lastAutoFocusedScreenShareTrack =
    React.useRef<TrackReferenceOrPlaceholder | null>(null)

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  )

  const widgetUpdate = (state: WidgetState) => {
    log.debug("updating widget state", state)
    setWidgetState(state)
  }

  const layoutContext = useCreateLayoutContext()

  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare)

  const focusTrack = usePinnedTracks(layoutContext)?.[0]
  const carouselTracks = tracks.filter(
    (track) => !isEqualTrackRef(track, focusTrack),
  )

  React.useEffect(() => {
    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      log.debug("Auto set screen share focus:", {
        newScreenShareTrack: screenShareTracks[0],
      })
      layoutContext.pin.dispatch?.({
        msg: "set_pin",
        trackReference: screenShareTracks[0],
      })
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0]
    } else if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some(
        (track) =>
          track.publication.trackSid ===
          lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
      )
    ) {
      log.debug("Auto clearing screen share focus.")
      layoutContext.pin.dispatch?.({ msg: "clear_pin" })
      lastAutoFocusedScreenShareTrack.current = null
    }
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (tr) =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source,
      )
      if (
        updatedFocusTrack !== focusTrack &&
        isTrackReference(updatedFocusTrack)
      ) {
        layoutContext.pin.dispatch?.({
          msg: "set_pin",
          trackReference: updatedFocusTrack,
        })
      }
    }
  }, [
    screenShareTracks
      .map(
        (ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`,
      )
      .join(),
    focusTrack?.publication?.trackSid,
  ])

  React.useEffect(() => {
    if (focusTrack == null && !isMobile) {
      const agentTrack = tracks.find(
        (track) =>
          track.source === Track.Source.Camera &&
          track.participant.isSpeaking &&
          track.participant.isAgent,
      )
      if (agentTrack) {
        layoutContext.pin.dispatch?.({
          msg: "set_pin",
          trackReference: agentTrack,
        })
      }
    }
  }, [tracks])

  const closeChat = React.useCallback(() => {
    setShowChat(false)
  }, [])

  const openChat = React.useCallback(() => {
    setShowChat(true)
  }, [])

  const toggleChat = React.useCallback(() => {
    setShowChat((prev) => !prev)
  }, [])

  const handleDisconnect = () => {
    room.disconnect()
    onDisconnected?.(null)
  }

  const localCameraTrack = tracks.find(
    (track) =>
      track.participant.isLocal && track.source === Track.Source.Camera,
  )?.publication

  const Layout = isMobile ? MobileLayout : WebLayout

  const { isEnabled, primaryColor } = useCustomBranding()

  if (!hostJoined)
    return (
      <React.Fragment>
        <CallLoading agent={context.agent} />
        <Overlay open={true} />
      </React.Fragment>
    )

  return (
    <div
      className="lk-video-conference relative"
      style={{
        padding: isEmbed ? 0 : 25,
        backgroundColor: "transparent",
      }}
      {...props}
    >
      <Background />
      {isWeb() && (
        <LayoutContextProvider
          value={layoutContext}
          onWidgetChange={widgetUpdate}
        >
          <Layout
            tracks={tracks.filter(
              (track) =>
                !(
                  track.participant.isAgent &&
                  track.participant.attributes &&
                  track.participant.attributes["lk.agent.state"]
                ),
            )}
            showChat={showChat}
            isEmbed={isEmbed}
            audioState={context.audioEnabled ?? true}
            videoState={context.videoEnabled ?? true}
            localCameraTrack={localCameraTrack}
            handleDisconnect={handleDisconnect}
            toggleChat={toggleChat}
            duration={duration}
            maxDuration={maxDuration}
            closeChat={closeChat}
            openChat={openChat}
            chatMessages={chatMessages}
            isSending={isSending}
            send={send}
            update={update}
            SettingsComponent={SettingsComponent}
            widgetState={widgetState}
            agent={context.agent}
          />
        </LayoutContextProvider>
      )}
      <RoomAudioRenderer />
      <ConnectionStateToast />
      <Overlay open={true} />
    </div>
  )
}
